import styled, { css } from 'styled-components';

// Estilos comunes para todos los botones
const baseStyles = css`
  display: ${({ $fullWidth }) => ($fullWidth ? 'flex' : 'inline-flex')};
  flex-shrink: 0;
  gap: 4px;
  align-items: center;
  font-variant-numeric: lining-nums tabular-nums;
  font-feature-settings: 'ss01' on, 'ss05' on, 'ss06' on, 'ss07' on;
  font-family: MonaSans, sans-serif;
  font-style: normal;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  text-box: trim-both cap alphabetic;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  justify-content: ${({ $contentAlign }) => {
    switch ($contentAlign) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      case 'space-between':
        return 'space-between';
      case 'center':
      default:
        return 'center';
    }
  }};
  height: ${({ size }) => {
    switch (size) {
      case 'xs':
        return '24px';
      case 's':
        return '28px';
      case 'm':
        return '32px';
      case 'l':
        return '40px';
      case 'xl':
        return '48px';
      default:
        return '32px'; // Default to m size
    }
  }};
  width: ${({ size, $iconOnly, $fullWidth }) => {
    if ($fullWidth) {
      return '100%';
    }
    if ($iconOnly) {
      switch (size) {
        case 'xs':
          return '24px';
        case 's':
          return '28px';
        case 'm':
          return '32px';
        case 'l':
          return '40px';
        case 'xl':
          return '48px';
        default:
          return '32px'; // Default to m size
      }
    }
    return 'auto';
  }};
  padding: ${({ size, $iconOnly }) => {
    if ($iconOnly) {
      return '0';
    }
    switch (size) {
      case 'xs':
        return '0 4px';
      case 's':
        return '0 6px';
      case 'm':
        return '0 6px';
      case 'l':
        return '0 8px';
      case 'xl':
        return '0 8px';
      default:
        return '0 6px'; // Default to m size
    }
  }};
  font-size: ${({ size }) => {
    switch (size) {
      case 'xs':
        return '14px';
      case 's':
        return '14px';
      case 'm':
        return '14px';
      case 'l':
        return '16px';
      case 'xl':
        return '16px';
      default:
        return '14px'; // Default to m size
    }
  }};
  line-height: ${({ size }) => {
    switch (size) {
      case 'xs':
        return '14px';
      case 's':
        return '14px';
      case 'm':
        return '14px';
      case 'l':
        return '16px';
      case 'xl':
        return '16px';
      default:
        return '14px'; // Default to m size
    }
  }};

    /* Cambiar cursor y opacidad si está deshabilitado */
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}
`;

// Estilos condicionales basados en props
const variantStyles = css`
  ${({ $variant, theme }) => {
    switch ($variant) {
      default:
        return css`
          background-color: ${theme.fill.inverseMain};
          border: 1px solid transparent;
          color: ${theme.colors.inverseMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.fill.inverseWeak};
          }
        `;
      case 'defaultInverse':
        return css`
          background-color: ${theme.fill.defaultMain};
          border: 1px solid transparent;
          color: ${theme.colors.defaultMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.palette.alabaster[100]};
          }
        `;
      case 'outline':
        return css`
          background-color: transparent;
          border: 1px solid ${theme.border.defaultWeak};
          color: ${theme.colors.defaultMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.palette.alabaster[100]};
          }
        `;
      case 'outlineInverse':
        return css`
          background-color: transparent;
          border: 1px solid ${theme.colors.inverseWeak};
          color: ${theme.colors.inverseMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.palette.alabaster[950]};
          }
        `;
      case 'ghost':
        return css`
          background-color: transparent;
          border: 1px solid transparent;
          color: ${theme.colors.defaultMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.palette.alabaster[100]};
          }
        `;
      case 'ghostInverse':
        return css`
          background-color: transparent;
          border: 1px solid transparent;
          color: ${theme.colors.inverseMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.palette.alabaster[950]};
          }
        `;
      case 'defaultDanger':
        return css`
          background-color: ${theme.fill.elserrorAlphaMain16};
          border: 1px solid transparent;
          color: ${theme.colors.errorMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.fill.elserrorAlphaMain32};
          }
        `;
      case 'outlineDanger':
        return css`
          background-color: transparent;
          border: 1px solid ${theme.fill.elserrorAlphaMain24};
          color: ${theme.colors.errorMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.fill.elserrorAlphaMain16};
          }
        `;
      case 'ghostDanger':
        return css`
          background-color: transparent;
          border: 1px solid transparent;
          color: ${theme.colors.errorMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.fill.elserrorAlphaMain24};
          }
        `;
      case 'defaultDangerInverse':
        return css`
          background-color: ${theme.fill.elserrorAlphaMain24};
          border: 1px solid transparent;
          color: ${theme.colors.errorMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.fill.elserrorAlphaMain32};
          }
        `;
      case 'outlineDangerInverse':
        return css`
          background-color: transparent;
          border: 1px solid ${theme.fill.elserrorAlphaMain48};
          color: ${theme.colors.errorMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.fill.elserrorAlphaMain24};
          }
        `;
      case 'ghostDangerInverse':
        return css`
          background-color: transparent;
          border: 1px solid transparent;
          color: ${theme.colors.errorMain};
          transition: all 0.3s ease-in-out;

          &:hover {
            background-color: ${theme.fill.elserrorAlphaMain32};
          }
        `;
    }
  }}
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !['sizeTablet', 'sizeMobile'].includes(prop)
})`
  ${baseStyles}
  ${variantStyles}

  /* Apply tablet variant styles when screen size is between sm and lg breakpoints */
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    ${({ sizeTablet, $iconOnly, $fullWidth }) =>
    sizeTablet &&
    `
        height: ${(() => {
      switch (sizeTablet) {
        case 'xs': return '24px';
        case 's': return '28px';
        case 'm': return '32px';
        case 'l': return '40px';
        case 'xl': return '48px';
        default: return '32px';
      }
    })()};
        
        width: ${$fullWidth ? '100%' : ($iconOnly ? (() => {
      switch (sizeTablet) {
        case 'xs': return '24px';
        case 's': return '28px';
        case 'm': return '32px';
        case 'l': return '40px';
        case 'xl': return '48px';
        default: return '32px';
      }
    })() : 'auto')};
        
        padding: ${$iconOnly ? '0' : (() => {
      switch (sizeTablet) {
        case 'xs': return '0 4px';
        case 's': return '0 6px';
        case 'm': return '0 6px';
        case 'l': return '0 8px';
        case 'xl': return '0 8px';
        default: return '0 6px';
      }
    })()};
        
        font-size: ${(() => {
      switch (sizeTablet) {
        case 'xs': return '14px';
        case 's': return '14px';
        case 'm': return '14px';
        case 'l': return '16px';
        case 'xl': return '16px';
        default: return '14px';
      }
    })()};
      `}
  }

  /* Apply mobile variant styles when screen size is below md breakpoint */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    ${({ sizeMobile, $iconOnly, $fullWidth }) =>
    sizeMobile &&
    `
        height: ${(() => {
      switch (sizeMobile) {
        case 'xs': return '24px';
        case 's': return '28px';
        case 'm': return '32px';
        case 'l': return '40px';
        case 'xl': return '48px';
        default: return '32px';
      }
    })()};
        
        width: ${$fullWidth ? '100%' : ($iconOnly ? (() => {
      switch (sizeMobile) {
        case 'xs': return '24px';
        case 's': return '28px';
        case 'm': return '32px';
        case 'l': return '40px';
        case 'xl': return '48px';
        default: return '32px';
      }
    })() : 'auto')};
        
        padding: ${$iconOnly ? '0' : (() => {
      switch (sizeMobile) {
        case 'xs': return '0 4px';
        case 's': return '0 6px';
        case 'm': return '0 6px';
        case 'l': return '0 8px';
        case 'xl': return '0 8px';
        default: return '0 6px';
      }
    })()};
        
        font-size: ${(() => {
      switch (sizeMobile) {
        case 'xs': return '14px';
        case 's': return '14px';
        case 'm': return '14px';
        case 'l': return '16px';
        case 'xl': return '16px';
        default: return '14px';
      }
    })()};
      `}
  }

  .Icon {
    height: 20px;
    margin: 2px;
  }
`;

export default Button;
